import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Users } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

const fmt = (n) => (n || 0).toLocaleString();
const FALLBACK = "https://images.unsplash.com/photo-1504609813442-a8924e83f76e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

export default function Community() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/communities/${slug}`);
      setData(data);
    } catch (err) {
      console.error("community load failed", err);
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const toggleJoin = async () => {
    if (!data?.community) return;
    setBusy(true);
    try {
      const action = data.community.is_member ? "leave" : "join";
      const { data: res } = await api.post(`/communities/${slug}/${action}`);
      setData((d) => ({ ...d, community: { ...d.community, is_member: res.is_member, member_count: res.member_count } }));
    } catch (err) {
      console.error("join failed", err);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40">Loading…</div>;
  if (!data?.community) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40">Community not found.</div>;

  const c = data.community;
  const posts = data.posts || [];

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="community-detail">
      <div className="relative h-52">
        <img src={c.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
        <button onClick={() => nav(-1)} data-testid="community-back" className="absolute top-5 left-4 w-9 h-9 rounded-full crystal-glass flex items-center justify-center text-cream z-10">
          <ArrowLeft size={18} />
        </button>
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Community</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">{c.emoji} {c.name}</h1>
          <p className="text-cream/70 text-sm">{c.tagline}</p>
          <div className="mt-3 flex items-center gap-4">
            <div className="flex items-center gap-4 text-xs font-bold">
              <span className="text-papaya flex items-center gap-1"><Users size={14} weight="fill" /> {fmt(c.member_count)}</span>
              <span className="text-cream/70">{fmt(c.post_count)} posts</span>
              <span className="text-cream/70">{fmt(c.creator_count)} creators</span>
            </div>
            <button
              onClick={toggleJoin}
              disabled={busy}
              data-testid="community-join-btn"
              className={`ml-auto px-5 py-2 rounded-full text-xs font-black tracking-[0.15em] uppercase transition-colors ${c.is_member ? "bg-white/10 text-cream border border-white/20" : "bg-papaya text-ink"}`}
            >
              {busy ? "…" : c.is_member ? "Joined" : "Join"}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 mt-4">
        <button
          onClick={() => nav(`/create?tag=${c.hashtags[0]}`)}
          data-testid="community-post-btn"
          className="w-full py-3 rounded-full bg-papaya/15 border border-papaya/40 text-papaya text-xs font-black tracking-[0.18em] uppercase flex items-center justify-center gap-2 hover:bg-papaya/25 transition-colors"
        >
          ✎ Post to {c.name}
        </button>
      </div>

      <section className="px-3 mt-5">
        <div className="flex items-center justify-between px-2 mb-3">
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold">Latest in {c.name}</p>
          <Link to={`/tag/${c.hashtags[0]}`} data-testid="community-tag-link" className="text-papaya text-[11px] font-black">#{c.hashtags[0]}</Link>
        </div>
        {posts.length === 0 ? (
          <div className="text-center py-16 px-6">
            <p className="text-cream/50 text-sm">No posts here yet.</p>
            <p className="text-cream/40 text-xs mt-1">Post with #{c.hashtags[0]} to be the first in {c.name}.</p>
            <Link to="/create" data-testid="community-create-cta" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.15em] uppercase">Create a dance</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="community-posts">
            {posts.map((p) => (
              <Link key={p.id} to={`/p/${p.id}`} data-testid={`community-post-${p.id}`} className="group relative aspect-[9/14] rounded-2xl overflow-hidden bg-white/5">
                {p.media_type === "video" ? (
                  <video src={fileUrl(p.media_path)} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={p.media_path ? fileUrl(p.media_path) : FALLBACK} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink to-transparent">
                  <p className="text-cream text-[11px] font-bold line-clamp-1">@{p.author?.username}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
